import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import {
  ShipmentListInitialState,
  ShipmentListLoad,
  ShipmentStatisticsLoad,
  UpdateDeliveryNoteAlertList,
  UpdateLoadAlertList,
  UpdateTransportOrderAlertList,
  UpdateWaybillAlertList,
} from './shipment-list.actions';
import { NotificationService } from '../../../../../common/services';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { ShipmentListService } from './shipment-list.service';
import { ShipmentListSearchCriteria } from '../models/shipment.model';
import { Shipment, ShipmentListDto, ShipmentStatisticsModel } from '../models/shipment-list/shipment-list.model';
import { StatisticFilterKeysEnum, StatisticFilterModel } from '../models/shipment-filters.model';
import { ShipmentQuickFilterModel } from '../../models/shipment-common-models';
import { ListSearchCriteriaFacade, ListSearchCriteriaType } from '../../../list-search-criteria';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { DEVIATIONS, INFOS, MILESTONES } from '../constants/shipment-statistics.constant';

export type ShipmentListStateModel = {
  statistic: ShipmentStatisticsModel;
  shipments: ShipmentListDto;
  mapThreshold: number;
  loaded: boolean;
  statisticLoaded: boolean;
};

const initialState: ShipmentListStateModel = {
  statistic: {
    milestones: MILESTONES,
    deviations: DEVIATIONS,
    infos: INFOS,
  },
  shipments: {
    items: [],
    size: null,
    pageNumber: null,
    numberOfElements: null,
    totalElements: null,
    totalPages: null,
  },
  mapThreshold: null,
  loaded: false,
  statisticLoaded: false,
};

@State<ShipmentListStateModel>({
  name: 'shipmentList',
  defaults: initialState,
})
@Injectable()
export class ShipmentListState {
  private stopStatisticsRequest$: Subject<void> = new Subject();

  constructor(
    private shipmentListService: ShipmentListService,
    private notificationService: NotificationService,
    private listSearchCriteriaFacade: ListSearchCriteriaFacade,
  ) {}

  @Selector()
  static getListDto(state: ShipmentListStateModel): ShipmentListDto {
    return state.shipments;
  }
  @Selector()
  static getItems(state: ShipmentListStateModel): Shipment[] {
    return state.shipments.items;
  }
  @Selector()
  static getInfoStatistics(state: ShipmentListStateModel): ShipmentQuickFilterModel[] {
    return state.statistic.infos;
  }

  @Selector()
  static getMilestoneStatistics(state: ShipmentListStateModel): ShipmentQuickFilterModel[] {
    return state.statistic.milestones;
  }

  @Selector()
  static getDeviationStatistics(state: ShipmentListStateModel): ShipmentQuickFilterModel[] {
    return state.statistic.deviations;
  }
  @Selector()
  static getLoaded(state: ShipmentListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getStatisticLoaded(state: ShipmentListStateModel): boolean {
    return state.statisticLoaded;
  }

  @Selector()
  static getTotalElements(state: ShipmentListStateModel): number {
    return state.shipments.totalElements;
  }

  @Selector()
  static getMapThreshold(state: ShipmentListStateModel): number {
    return state.mapThreshold;
  }

  @Selector()
  static getIsMapThresholdReached(state: ShipmentListStateModel): boolean {
    return state.mapThreshold >= state.shipments.totalElements;
  }

  @Action(ShipmentListLoad)
  getShipmentList(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { searchCriteria, dialogRef } }: ShipmentListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentListService.getShipmentList(searchCriteria).pipe(
      map((shipmentListStateModel: ShipmentListStateModel) => {
        ctx.patchState({ ...shipmentListStateModel, loaded: true });

        if (dialogRef) {
          this.notificationService.success('api.shipmentsAlerts.success.update');
          dialogRef.close();
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentStatisticsLoad)
  getShipmentStatistics(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { searchCriteria } }: ShipmentListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const activeStatistic = this.getActiveStatisticFilters(
      searchCriteria.shipmentStatisticFilter,
      ctx.getState().statistic,
    );

    ctx.patchState({ statistic: activeStatistic, statisticLoaded: false });

    this.stopStatisticsRequest$.next();

    return this.shipmentListService.getShipmentStatistics(searchCriteria).pipe(
      takeUntil(this.stopStatisticsRequest$),
      map((statistic: ShipmentStatisticsModel) => {
        ctx.patchState({ statistic, statisticLoaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        ctx.patchState({ statisticLoaded: true });
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(ShipmentListInitialState)
  setInitialState(ctx: StateContext<ShipmentListStateModel>): void {
    this.stopStatisticsRequest$.next();

    ctx.setState(initialState);
  }

  @Action(UpdateWaybillAlertList)
  updateWaybillShipmentAlert(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { shipmentWaybillAlertUpdatePayload, dialogRef } }: UpdateWaybillAlertList,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentListService.updateWaybillAlertList(shipmentWaybillAlertUpdatePayload).pipe(
      withLatestFrom(this.listSearchCriteriaFacade.getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)),
      map(([_, searchCriteria]: [null, ShipmentListSearchCriteria]) => {
        ctx.dispatch(new ShipmentListLoad({ searchCriteria, dialogRef }));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UpdateTransportOrderAlertList)
  updateTransportOrderShipmentAlert(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { shipmentTransportOrderAlertUpdatePayload, dialogRef } }: UpdateTransportOrderAlertList,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentListService.updateTransportOrderAlertList(shipmentTransportOrderAlertUpdatePayload).pipe(
      withLatestFrom(this.listSearchCriteriaFacade.getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)),
      map(([_, searchCriteria]: [null, ShipmentListSearchCriteria]) => {
        ctx.dispatch(new ShipmentListLoad({ searchCriteria, dialogRef }));
        ctx.dispatch(new ShipmentStatisticsLoad({ searchCriteria }));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UpdateLoadAlertList)
  updateLoadShipmentAlert(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { shipmentLoadAlertUpdatePayload, dialogRef } }: UpdateLoadAlertList,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentListService.updateLoadAlertList(shipmentLoadAlertUpdatePayload).pipe(
      withLatestFrom(this.listSearchCriteriaFacade.getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)),
      map(([_, searchCriteria]: [null, ShipmentListSearchCriteria]) => {
        ctx.dispatch(new ShipmentListLoad({ searchCriteria, dialogRef }));
        ctx.dispatch(new ShipmentStatisticsLoad({ searchCriteria }));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UpdateDeliveryNoteAlertList)
  updateDeliveryNoteShipmentAlert(
    ctx: StateContext<ShipmentListStateModel>,
    { payload: { shipmentDeliveryNoteAlertUpdatePayload, dialogRef } }: UpdateDeliveryNoteAlertList,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentListService.updateDeliveryNoteAlertList(shipmentDeliveryNoteAlertUpdatePayload).pipe(
      withLatestFrom(this.listSearchCriteriaFacade.getSearchCriteriaByListType(ListSearchCriteriaType.Shipment)),
      map(([_, searchCriteria]: [null, ShipmentListSearchCriteria]) => {
        ctx.dispatch(new ShipmentListLoad({ searchCriteria, dialogRef }));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  private getActiveStatisticFilters(
    filters: StatisticFilterModel,
    statistic: ShipmentStatisticsModel,
  ): ShipmentStatisticsModel {
    const KEYS_MAP: Record<StatisticFilterKeysEnum, keyof ShipmentStatisticsModel> = {
      [StatisticFilterKeysEnum.Milestone]: 'milestones',
      [StatisticFilterKeysEnum.Deviation]: 'deviations',
      [StatisticFilterKeysEnum.Info]: 'infos',
    };

    if (!filters || !statistic) {
      return statistic;
    }

    return Object.keys(filters).reduce(
      (acc: ShipmentStatisticsModel, key: StatisticFilterKeysEnum) => {
        if (filters[key]) {
          acc = {
            ...acc,
            [KEYS_MAP[key]]: acc[KEYS_MAP[key]].map((item: ShipmentQuickFilterModel) => ({
              ...item,
              active: item.name === filters[key],
            })),
          };
        }
        return acc;
      },
      { ...statistic },
    );
  }
}
